import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import FileLibrary from './index';

// Staff-dashboard File Library: 3 sections — Public (company-wide shared pool: everyone sees the same
// tree, can create folders and upload into anyone's folders), Legal clients, Accounting clients.
// Legal/Accounting list every client of that firm as a folder; clicking opens that client's library
// (the same files shown on the client's own Dashboard). The 'personal' scope key is kept for
// backwards-compat — since 2026-07-03 it IS the public pool.
//
// Drag & drop BETWEEN sections: rows dragged out of a library (they carry 'application/x-slf-node')
// can be dropped on the Public pill (→ the shared root) or — after hovering a firm pill auto-switches
// to its client list — onto a client row (→ that client's library). The move carries the whole subtree.
type FirmClient = { id: string; name: string };

const NODE_MIME = 'application/x-slf-node';
const hasNode = (e: React.DragEvent) => Array.from(e.dataTransfer.types || []).includes(NODE_MIME);

export default function StaffFileLibrary() {
  const { t } = useTranslation();
  const [section, setSection] = useState<'personal' | 'legal' | 'accounting'>('personal');
  const [clients, setClients] = useState<FirmClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState<FirmClient | null>(null);
  const [q, setQ] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);       // bumped after a cross-scope move → remount library
  const [pillOver, setPillOver] = useState<string | null>(null);
  const [rowOver, setRowOver] = useState<string | null>(null);

  useEffect(() => {
    setSel(null); setQ('');
    if (section === 'personal') { setClients([]); return; }
    setLoading(true);
    api.filesClients(section).then((d: any) => setClients(d.clients || [])).catch(() => setClients([])).finally(() => setLoading(false));
  }, [section]);

  const moveTo = async (e: React.DragEvent, scope: string, after?: () => void) => {
    e.preventDefault(); setPillOver(null); setRowOver(null);
    const raw = e.dataTransfer.getData(NODE_MIME);
    if (!raw) return;
    // The dragged row(s) carry one id, or a JSON array when several were Ctrl/Shift-selected.
    let ids: string[];
    try { const parsed = JSON.parse(raw); ids = Array.isArray(parsed) ? parsed.filter(Boolean) : [raw]; } catch { ids = [raw]; }
    if (!ids.length) return;
    try {
      if (ids.length > 1) await api.filesMoveBulk(ids, '', scope);
      else await api.filesMove(ids[0], '', scope);
      setRefreshKey(k => k + 1);
      after?.();
    } catch (err: any) { alert(err.message); }
  };

  const tab = (k: 'personal' | 'legal' | 'accounting', icon: string, label: string) => (
    <button onClick={() => setSection(k)}
      onDragOver={e => {
        if (!hasNode(e)) return;
        // Hovering a pill mid-drag navigates there (like dragging over a Finder sidebar item), so the
        // user can then drop on a client row. The Personal pill is itself a drop target.
        if (section !== k) setSection(k); else if (sel) setSel(null);
        if (k === 'personal') { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setPillOver(k); }
      }}
      onDragLeave={() => setPillOver(p => (p === k ? null : p))}
      onDrop={k === 'personal' ? (e => { if (hasNode(e)) moveTo(e, 'personal', () => setSection('personal')); }) : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 999, border: '1px solid', fontSize: 13, fontWeight: 700, cursor: 'pointer', borderColor: pillOver === k ? 'var(--accent)' : section === k ? 'var(--accent)' : 'var(--border-strong)', background: section === k ? 'var(--accent)' : pillOver === k ? 'var(--bg-active)' : 'transparent', color: section === k ? '#fff' : 'var(--text-secondary)', boxShadow: pillOver === k ? '0 0 0 2px var(--accent)' : undefined }}>
      <i className={`ti ti-${icon}`} />{label}
    </button>
  );

  const filtered = clients.filter(c => !q.trim() || c.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {tab('personal', 'users', t('fl_personal'))}
        {tab('legal', 'scale', t('fl_legal'))}
        {tab('accounting', 'calculator', t('fl_accounting'))}
      </div>

      {section === 'personal' ? (
        <FileLibrary key={`personal-${refreshKey}`} scope="personal" />
      ) : sel ? (
        <div>
          <button onClick={() => setSel(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', marginBottom: 12 }}>
            <i className="ti ti-arrow-left" />{section === 'legal' ? t('fl_legal') : t('fl_accounting')}
          </button>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 12 }}>{sel.name}</div>
          <FileLibrary key={`${sel.id}-${refreshKey}`} scope={`client:${sel.id}`} />
        </div>
      ) : (
        <>
          <div style={{ position: 'relative', marginBottom: 12, maxWidth: 280 }}>
            <i className="ti ti-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--text-tertiary)' }} />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('fl_search_client')} style={{ width: '100%', padding: '8px 10px 8px 30px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-app)', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          {loading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22 }} /></div>
            : filtered.length === 0 ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)', fontSize: 13 }}>{t('fl_no_clients')}</div>
            : (
              <>
                <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 6 }}>{filtered.length} {t('fl_clients_count')}</div>
                <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                  {filtered.map((c, i) => (
                    <button key={c.id} onClick={() => setSel(c)}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'var(--bg-surface)')}
                      onDragOver={e => { if (hasNode(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setRowOver(c.id); } }}
                      onDragLeave={() => setRowOver(id => (id === c.id ? null : id))}
                      onDrop={e => { if (hasNode(e)) moveTo(e, `client:${c.id}`, () => setSel(c)); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none', borderTop: i ? '1px solid var(--border)' : 'none', background: rowOver === c.id ? 'var(--bg-active)' : 'var(--bg-surface)', cursor: 'pointer', outline: rowOver === c.id ? '2px solid var(--accent)' : 'none', outlineOffset: -2 }}>
                      <i className="ti ti-folder" style={{ fontSize: 18, color: '#E8A33D', flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                      <i className="ti ti-chevron-right" style={{ fontSize: 15, color: 'var(--text-tertiary)', flexShrink: 0 }} />
                    </button>
                  ))}
                </div>
              </>
            )}
        </>
      )}
    </div>
  );
}
